import type { PaymentAmountSelection } from "../amountSelection";
import type { PendingPayment } from "../checkoutTypes";
import type { DispatchProV2PaymentSummary, DispatchProV2TemporaryCard } from "../payment";
import { hasConfiguredSurcharge } from "../surcharge";

export const xplorPaymentFormId = "xplor-card-payment-form";

export type XplorPaymentTokenResponse = {
  payload?: {
    "mobile-jwt"?: {
      jwt?: string;
    };
  };
};

export type XplorSdk = {
  init: (configuration: {
    baseUrl: string;
    pk: string;
    paymentFormId: string;
    allowAutoComplete: boolean;
    clearFormOnSuccess: boolean;
  }) => void;
  getPaymentToken: () => Promise<XplorPaymentTokenResponse>;
  reset: () => void;
};

export function getXplorScriptUrl(gatewayUrl: string) {
  return new URL("/js-sdk/js/clearent-host.js", gatewayUrl).toString();
}

export function getXplorSdk() {
  return (globalThis as typeof globalThis & { ClearentSDK?: XplorSdk }).ClearentSDK;
}

export function mountXplorForm(sdk: XplorSdk, payment: DispatchProV2PaymentSummary) {
  sdk.init({
    baseUrl: payment.gatewayUrl,
    pk: payment.publicKey,
    paymentFormId: xplorPaymentFormId,
    allowAutoComplete: false,
    clearFormOnSuccess: true,
  });

  let reset = false;
  return () => {
    if (reset) return;

    reset = true;
    try {
      sdk.reset();
    } catch {
      return;
    }
  };
}

export async function getXplorPaymentToken(sdk: XplorSdk) {
  const response = await sdk.getPaymentToken();
  const token = response?.payload?.["mobile-jwt"]?.jwt;
  if (!token) throw new Error("Secure card form did not return a payment token.");

  return token;
}

export function createXplorPendingPayment({
  customerId,
  invoiceId,
  payment,
  paymentAmount,
  tempCard,
}: {
  customerId: string;
  invoiceId?: string;
  payment: DispatchProV2PaymentSummary;
  paymentAmount: PaymentAmountSelection;
  tempCard: DispatchProV2TemporaryCard;
}): PendingPayment {
  return {
    request: {
      customerId,
      invoiceId,
      amount: paymentAmount.amount,
      applySurcharge: hasConfiguredSurcharge(payment.surchargePercentage),
      dispatchProV2Card: {
        id: tempCard.id,
      },
    },
    card: tempCard,
  };
}
